// store/template.ts
import { Module } from 'vuex';
import { Template, TemplateState } from '@/types';
import { getTemplates, reprocessDocumentGeneration } from '@/store/services/document/templatesServices';
import {organizeStatusTemplates, sortDocumentsByBaseName, reorderContractDocuments, UON} from '@/utils';
import store from '@/store';

const templateModule: Module<TemplateState, any> = {
    namespaced: true,

    state: (): TemplateState => ({
        templates: [],
        selectedTemplates: [],
        previewTemplate:[],
        isLoading: false,
        error: null,
        FinancingAgreement : null,
        FinancingFolder : null,
    }),

    getters: {
getGroupedTemplates: (state) => (signature?: string, isPrintDocsModal?: boolean) => {
    // Group templates by package name
    const groupedTemplates = state.templates.reduce((acc, template) => {
        if (!acc[template.packageName]) {
            acc[template.packageName] = [];
        }
        acc[template.packageName].push(template);
        return acc;
    }, {} as { [key: string]: Template[] });

    const autresTemplates = groupedTemplates["AUTRES"] || [];
    state.FinancingAgreement = autresTemplates.find((template) => template.templateName.includes("DOC_CPT_056")) || null;
    store.dispatch('templateModule/SET_FinancingFolder', {
        DDFE: autresTemplates.find((template) => template.templateName.includes("DOC_CPT_046")) || null,
        DDFP: autresTemplates.find((template) => template.templateName.includes("DOC_CPT_045")) || null
    });
    groupedTemplates["AUTRES"] = autresTemplates.filter(
        (template) => !["DOC_CPT_056", "DOC_CPT_046", "DOC_CPT_045"].includes(template.templateName)
    );

    let finalGroupedTemplates = groupedTemplates;

    if (signature) {
        finalGroupedTemplates = Object.keys(groupedTemplates).reduce((filtered, packageName) => {
            const filteredTemplates = groupedTemplates[packageName].filter(
                (template) => template.signatureType === signature
            );
            if (filteredTemplates.length > 0) {
                filtered[packageName] = filteredTemplates;
            }
            return filtered;
        }, {} as { [key: string]: Template[] });
    }

    if (finalGroupedTemplates["AUTRES"]) {
        const autres = { AUTRES: finalGroupedTemplates["AUTRES"] };
        delete finalGroupedTemplates["AUTRES"];
        finalGroupedTemplates = { ...finalGroupedTemplates, ...autres };
    }

    // Object.keys(finalGroupedTemplates).forEach((packageName) => {
    //     const templates = finalGroupedTemplates[packageName];

    //     const fileNameIndices: { [fileName: string]: number[] } = {};
    //     templates.forEach((template, index) => {
    //         if (template.fileName.includes("(BAILLEUR)")) {
    //             if (!fileNameIndices[template.fileName]) {
    //                 fileNameIndices[template.fileName] = [];
    //             }
    //             fileNameIndices[template.fileName].push(index);
    //         }
    //     });

    //     Object.values(fileNameIndices).forEach((indices) => {
    //         if (indices.length >= 2) {
    //             const [firstIdx, secondIdx] = indices;
    //             [templates[firstIdx], templates[secondIdx]] = [templates[secondIdx], templates[firstIdx]];
    //         }
    //     });

    //     finalGroupedTemplates[packageName] = templates;
    // });

    Object.keys(finalGroupedTemplates).forEach((packageName) => {
        finalGroupedTemplates[packageName] = sortDocumentsByBaseName(
            finalGroupedTemplates[packageName],
            packageName
        );
        if (
            packageName.startsWith("LOA") ||
            packageName.startsWith("CBM") ||
            packageName.startsWith("LLD")
        ) {
            finalGroupedTemplates[packageName] = reorderContractDocuments(
                finalGroupedTemplates[packageName],
                packageName
            );
        }
    });

    return finalGroupedTemplates;
},       
        getTemplates: (state) => state.templates.filter(template => !["DOC_CPT_056", "DOC_CPT_046", "DOC_CPT_045"].includes(template.templateName)
    ),
        getSelectedTemplates: (state) => state.selectedTemplates,
        getFinancingAgreement: (state) => state.FinancingAgreement,
        getFinancingFolder: (state) => state.FinancingFolder,
        isTemplateSelected: (state) => (processId: string) =>
            state.selectedTemplates.includes(processId)
    },

    mutations: {
        SET_TEMPLATES(state, templates: Template[]) {
            state.templates = templates;
        },
        SET_FinancingFolder(state, templates: Template[]) {

            state.FinancingFolder = templates[0];
        },
        SET_PREVIEW_TEMPLATE(state, template: Template) {
            state.previewTemplate = [template];
        },
        RESET_PREVIEW_TEMPLATE(state) {
            state.previewTemplate = [];
        },
        SET_LOADING(state, isLoading: boolean) {
            state.isLoading = isLoading;
        },
        SET_ERROR(state, error: string | null) {
            state.error = error;
        },
        TOGGLE_TEMPLATE_SELECTION(state, processId: string) {
            const index = state.selectedTemplates.indexOf(processId);
            if (index === -1) {
                state.selectedTemplates.push(processId);
            } else {
                state.selectedTemplates.splice(index, 1);
            }
        },
        CLEAR_SELECTION(state) {
            state.selectedTemplates = [];
        },
        SELECT_ALL_TEMPLATES(state) {
            state.selectedTemplates = state.templates.map(template => template.processId);
        },
        UPDATE_TEMPLATE_STATUS(state, template) {
            const templateIndex = state.templates.findIndex(t => t.resourceUid === template.resourceUid);
            if (templateIndex !== -1) {
                state.templates[templateIndex] = template;
            }
        }
    },

    actions: {

        setPreviewTemplates({ commit }, template:Template) {
            commit('SET_PREVIEW_TEMPLATE', template)
        },
        RESET_PREVIEW_TEMPLATE({ commit }) {
            commit('RESET_PREVIEW_TEMPLATE')
        },

        async fetchTemplates({ commit }, payload) {
            commit('SET_LOADING', true);
            try {
                const templates = await getTemplates(UON.SystemUid.odmOffer, "generatedDocument/checkDocumentGenerated", payload.entityId);

                const sortedTemplates = sortDocumentsByBaseName(templates.statusTemplates, payload.packageName);
                sortedTemplates.sort((a: any, b: any) => a.documentOrder - b.documentOrder);

                commit('SET_TEMPLATES', sortedTemplates);
                commit('SET_ERROR', null);
            } catch (error) {
                commit('SET_ERROR', 'Failed to fetch templates');
            } finally {
                commit('SET_LOADING', false);
            }
        },


        async retryFailedTemplate({ commit, state }, template: Template) {
            try {
                
                if (template) {
                    commit('SET_LOADING', true);
                    // Replace with actual retry API call
                    const updatedtemplate = await reprocessDocumentGeneration(UON.SystemUid.odmOffer, "generatedDocument/reprocessDocument/"+template.entityId, template);
                    const entityId = store.state.taskModule.offer?.resourceUid;
                    store.dispatch('templateModule/fetchTemplates', { entityId: entityId });
                  
                }
            } catch (error) {
                commit('SET_ERROR', 'Failed to retry template');
            } finally {
                commit('SET_LOADING', false);
            }
        },

        toggleSelection({ commit }, processId: string) {
            commit('TOGGLE_TEMPLATE_SELECTION', processId);
        },

        selectTemplate({ commit, getters }, processId: string) {
            if (!getters.isTemplateSelected(processId)) {
                commit('TOGGLE_TEMPLATE_SELECTION', processId);
            }
        },

        deselectTemplate({ commit, getters }, processId: string) {
            if (getters.isTemplateSelected(processId)) {
                commit('TOGGLE_TEMPLATE_SELECTION', processId);
            }
        },

        clearSelection({ commit }) {
            commit('CLEAR_SELECTION');
        },

        selectAll({ commit }) {
            commit('SELECT_ALL_TEMPLATES');
        },
        SET_FinancingFolder({ commit }, payload:{DDFE:Template,DDFP:Template}) {
            const {DDFE, DDFP} = payload;
            const customerType =store.getters["taskModule/isTaskAnalysis"] 
                    ? store.state.taskModule.selectedTask?.variables?.customerType 
                    : store.state.middleOfficeModule.offer?.associatedParties?.[0]?.associatedParty?.third?.objectType;
            const isCustomerTypePerson = customerType.toLowerCase().includes('person') 
            isCustomerTypePerson ? commit('SET_FinancingFolder', [DDFP]) : commit('SET_FinancingFolder', [DDFE])
        },
    }
};

export default templateModule;